import React, { useState, useEffect, useRef, useMemo } from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import { setTextVariantStyle } from 'common/utils/variantStyle';

/**
 * ExpandableText component renders a text with an expandable option if it overflows.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.variant='subtitle2'] - The text variant style.
 * @param {string} [props.buttonText=i18n.t('btn.show_more')] - The text for the expand button.
 * @param {string} props.text - The text content to display.
 * @param {number} [props.lineClamp=1] - The number of lines to clamp the text to.
 * @param {Object} [props.style] - Additional styles for the component.
 * @param {boolean} [props.hiddenExpandBtn=false] - Whether to hide the expand button.
 * @param {function} [props.onExpandChange] - Callback function triggered when the expand state changes.
 *
 * @returns {JSX.Element} The rendered ExpandableText component.
 */
const ExpandableText = ({
    variant = 'subtitle2',
    buttonText = i18n.t('btn.show_more'),
    text,
    lineClamp = 1,
    style,
    hiddenExpandBtn = false,
    onExpandChange
}) => {
    const textRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const lineHeight = useMemo(() => setTextVariantStyle(variant, 'line-height'), [variant]);
    const fontSize = useMemo(() => setTextVariantStyle(variant, 'font-size'), [variant]);
    const handleChange = e => {
        if (onExpandChange && typeof onExpandChange === 'function') {
            onExpandChange(e.target.checked);
        }
    };

    useEffect(() => {
        const checkOverflow = () => {
            const element = textRef.current;
            if (element) {
                const isContentOverflowing =
                    lineHeight * fontSize * lineClamp < element.clientHeight;
                setIsOverflowing(isContentOverflowing);
                if (onExpandChange && typeof onExpandChange === 'function') {
                    onExpandChange(!isContentOverflowing);
                }
            }
        };

        checkOverflow();
    }, [text, lineClamp, lineHeight, fontSize, onExpandChange]);

    return (
        <div css={css([expandableTextStyle, style])}>
            <div
                ref={textRef}
                css={ellipsisStyle({
                    lineClamp: isOverflowing ? lineClamp : 'unset',
                    variant
                })}
            >
                {text}
            </div>
            {isOverflowing && (
                <input
                    type="checkbox"
                    value="Read More"
                    css={expandBtnStyle({ variant, buttonText, hiddenExpandBtn })}
                    onChange={handleChange}
                />
            )}
        </div>
    );
};

ExpandableText.propTypes = {
    text: PropTypes.string.isRequired,
    lineClamp: PropTypes.number,
    style: PropTypes.object,
    onExpandChange: PropTypes.func
};

const expandableTextStyle = css`
    position: relative;
`;

const ellipsisStyle = ({ lineClamp, variant }) => css`
    position: relative;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${lineClamp};
    overflow: clip;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    ${setTextVariantStyle(variant)}

    &:has(+ input:checked) {
        -webkit-box-orient: unset;
        -webkit-line-clamp: unset;
        overflow: auto;
    }
`;

const expandBtnStyle = ({ variant, buttonText, hiddenExpandBtn }) => css`
    appearance: none;
    position: absolute;
    right: 0;
    text-align: center;
    text-decoration: underline;
    background-color: inherit;
    cursor: pointer;
    ${setTextVariantStyle(variant)}

    &:checked {
        ${hiddenExpandBtn ? 'display: none;' : ''}
    }

    &::before {
        content: '${buttonText}';
    }

    &:checked::before {
        content: '';
    }
`;

export default ExpandableText;
